import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { X, Play, Pause, ArrowCounterClockwise, ListChecks, SignOut, CheckCircle, Trash } from "@phosphor-icons/react";

const TYPES = {
  decision: { label: "Decision", color: "#D4AF37" },
  action: { label: "Action", color: "#3B82F6" },
  parking: { label: "Parking lot", color: "#F59E0B" },
};

function useTimer() {
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [running]);
  return { seconds, running, setRunning, reset: () => { setSeconds(0); setRunning(false); } };
}

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export function WorkshopToolbar({ title, context, onExit }) {
  const timer = useTimer();
  const [items, setItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form, setForm] = useState({ type: "decision", text: "", owner: "" });

  useEffect(() => {
    api.get("/workshop-items").then((r) => setItems(r.data)).catch(() => toast.error("Could not load workshop items"));
    document.documentElement.requestFullscreen?.().catch(() => {});
    const onFsChange = () => { if (!document.fullscreenElement) onExit(); };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  const capture = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/workshop-items", { ...form, context });
      setItems((prev) => [data, ...prev]);
      setForm({ ...form, text: "", owner: "" });
      toast.success(`${TYPES[form.type].label} captured`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleStatus = async (it) => {
    const { data } = await api.put(`/workshop-items/${it.id}`, { status: it.status === "open" ? "closed" : "open" });
    setItems(items.map((x) => (x.id === it.id ? data : x)));
  };

  const remove = async (id) => {
    await api.delete(`/workshop-items/${id}`);
    setItems(items.filter((x) => x.id !== id));
  };

  const openCount = items.filter((i) => i.status === "open").length;

  return (
    <>
      <div className="fixed top-0 inset-x-0 z-50 bg-[#0A0A0A] text-white flex items-center gap-4 px-5 py-2.5 border-b border-[#D4AF37]/40" data-testid="workshop-toolbar">
        <div className="shrink-0">
          <p className="text-[9px] uppercase tracking-[0.25em] text-[#D4AF37] leading-none">Workshop mode</p>
          <p className="font-display font-bold text-sm tracking-tight leading-tight">{title}</p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 border-l border-white/15 pl-4">
          <span className="font-mono text-lg text-[#D4AF37] w-[62px]" data-testid="workshop-timer">{fmt(timer.seconds)}</span>
          <button onClick={() => timer.setRunning(!timer.running)} data-testid="workshop-timer-toggle"
            className="p-1.5 rounded-sm bg-white/10 hover:bg-white/20 transition-colors">
            {timer.running ? <Pause size={13} weight="fill" /> : <Play size={13} weight="fill" />}
          </button>
          <button onClick={timer.reset} data-testid="workshop-timer-reset"
            className="p-1.5 rounded-sm bg-white/10 hover:bg-white/20 transition-colors"><ArrowCounterClockwise size={13} /></button>
        </div>
        <form onSubmit={capture} className="flex items-center gap-2 flex-1 min-w-0 border-l border-white/15 pl-4">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="workshop-type-select"
            className="text-[11px] rounded-sm px-2 py-1.5 border shrink-0"
            style={{ background: TYPES[form.type].color, color: form.type === "decision" ? "#0A0A0A" : "#fff", borderColor: TYPES[form.type].color }}>
            {Object.entries(TYPES).map(([k, t]) => <option key={k} value={k} className="text-black bg-white">{t.label}</option>)}
          </select>
          <input value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="workshop-text-input"
            placeholder="Capture a decision, action or parking-lot item…"
            className="flex-1 min-w-0 bg-white/10 text-white text-[12px] rounded-sm px-3 py-1.5 outline-none focus:ring-1 focus:ring-[#D4AF37] placeholder:text-white/35" />
          <input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} data-testid="workshop-owner-input"
            placeholder="Owner" className="w-24 bg-white/10 text-white text-[12px] rounded-sm px-2 py-1.5 outline-none focus:ring-1 focus:ring-[#D4AF37] placeholder:text-white/35" />
          <button type="submit" data-testid="workshop-capture-btn"
            className="text-[11px] bg-[#D4AF37] text-[#0A0A0A] font-semibold rounded-sm px-3 py-1.5 hover:bg-[#e0c258] transition-colors shrink-0">Capture</button>
        </form>
        <button onClick={() => setDrawerOpen(!drawerOpen)} data-testid="workshop-drawer-btn"
          className="flex items-center gap-1.5 text-[11px] border border-white/25 rounded-sm px-3 py-1.5 hover:border-[#D4AF37] transition-colors shrink-0">
          <ListChecks size={13} /> Items <span className="font-mono text-[#D4AF37]">{openCount}</span>
        </button>
        <button onClick={onExit} data-testid="workshop-exit-btn"
          className="flex items-center gap-1.5 text-[11px] bg-white/10 rounded-sm px-3 py-1.5 hover:bg-white/20 transition-colors shrink-0">
          <SignOut size={13} /> Exit
        </button>
      </div>

      {drawerOpen && (
        <div className="fixed top-[52px] bottom-0 right-0 w-[380px] z-50 bg-[#0A0A0A]/95 backdrop-blur-xl border-l border-[#D4AF37]/30 overflow-y-auto fade-up" data-testid="workshop-drawer">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-[#0A0A0A]">
            <p className="font-display font-bold text-white text-sm tracking-tight">Workshop outcomes</p>
            <button onClick={() => setDrawerOpen(false)} className="text-white/60 hover:text-white transition-colors"><X size={16} /></button>
          </div>
          {items.length === 0 && <p className="p-4 text-[12px] text-white/45">Nothing captured yet — use the toolbar to record decisions, actions and parking-lot items as the discussion happens.</p>}
          <div className="divide-y divide-white/8">
            {items.map((it) => (
              <div key={it.id} className={`px-4 py-3 ${it.status === "closed" ? "opacity-45" : ""}`} data-testid="workshop-item-row">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-sm"
                    style={{ background: TYPES[it.type]?.color, color: it.type === "decision" ? "#0A0A0A" : "#fff" }}>{TYPES[it.type]?.label}</span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleStatus(it)} title={it.status === "open" ? "Mark closed" : "Reopen"} data-testid={`workshop-item-toggle-${it.id}`}
                      className={`transition-colors ${it.status === "closed" ? "text-green-400" : "text-white/40 hover:text-green-400"}`}><CheckCircle size={15} weight={it.status === "closed" ? "fill" : "regular"} /></button>
                    <button onClick={() => remove(it.id)} data-testid={`workshop-item-delete-${it.id}`}
                      className="text-white/30 hover:text-red-400 transition-colors"><Trash size={14} /></button>
                  </div>
                </div>
                <p className={`text-[12px] text-white/85 mt-1.5 ${it.status === "closed" ? "line-through" : ""}`}>{it.text}</p>
                <p className="text-[10px] text-white/40 mt-1">
                  {it.owner && <span className="text-[#D4AF37]">{it.owner} · </span>}
                  {it.context === "map" ? "Enterprise Map" : "Roadmap"} · {it.created_by} · {new Date(it.ts).toLocaleTimeString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
